import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import LocalizationService from "services/localizationService";
import { action, computed, observable } from "mobx";
import type { interfaces } from "inversify";
import DetailViewModel from "viewModels/detailViewModel";
import type CardGatewayPaymentDetailResponse from "entities/cardGatewayPaymentDetailResponse";
import PaymentsRepository from "repositories/paymentsRepository";
import EnumService from "services/enumService";
import { Modules } from "config";
import CardGatewayPaymentClassificationResponse from "entities/cardGatewayPaymentClassificationResponse";
import OptionsService from "services/optionsService";
import TerminalType from "entities/terminalType";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ name: Router.Self, route: ":paymentId" })
export default class PaymentCardGatewayDetailViewModel extends DetailViewModel<CardGatewayPaymentDetailResponse> {
  protected readonly modelName = "paymentCardGatewayDetail";
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable protected itemId: string;

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }
  @computed
  get isAmountPositive() {
    return this.item?.bruttoAmount > 0;
  }
  @computed
  get variableSymbol() {
    return this.item?.variableSymbol;
  }

  constructor(
    itemId: string,
    private repository: PaymentsRepository,
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected optionsService: OptionsService
  ) {
    super(itemId);
  }

  onInitialize() {
    return super.onInitialize();
  }

  @watchBusy
  protected loadDetail(): Promise<CardGatewayPaymentDetailResponse> {
    return this.repository.getCardGatewayPaymentDetail(this.itemId);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.paymentCardGateway.${code}`, size);
  }

  translateCardAssociation(code: string, size?: number): string {
    return this.localizationService.translateModule(
      Modules.finance,
      `payments.paymentCardGateway.cardAssociationEnum.${code}`,
      size
    );
  }

  @action.bound
  translateTerminalType(value: TerminalType) {
    const types = this.optionsService.internalEnumOptions(TerminalType);
    const type = types.find(type => type.id === value)?.label;
    return type ?? emptyValue;
  }

  @action.bound
  translatePaymentTemplate(paymentClassification: CardGatewayPaymentClassificationResponse) {
    if (paymentClassification?.isReversal) {
      return this.translate("reversal");
    }
    if (paymentClassification?.paymentTemplateId) {
      const paymentTemplate = this.enumService
        .values("payment_templates")
        ?.find(paymentTemplate => paymentTemplate.id === paymentClassification.paymentTemplateId);

      if (!paymentTemplate) {
        return;
      }

      return paymentTemplate.messageTemplate;
    }
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new PaymentCardGatewayDetailViewModel(
        itemId,
        container.get(PaymentsRepository),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(OptionsService)
      );
  }
}
